/**
 * ColorFilterButton - Mana icon button for color filtering
 *
 * Uses mana-font icons with proper visibility styling for dark backgrounds.
 * Supports WUBRG colors plus Multi (5-color wheel) and Colorless (diamond).
 * White and black mana get special glow effects for visibility.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { Color } from '@/types/card';

/** Special color filter values beyond WUBRG */
export type SpecialColorFilter = 'multi' | 'colorless';

/** All supported color filter values */
export type ColorFilterValue = Color | SpecialColorFilter;

export interface ColorFilterButtonProps {
  /** MTG color code or special filter (multi/colorless) */
  color: ColorFilterValue;
  /** Whether the filter is active */
  isActive: boolean;
  /** Click handler */
  onClick: () => void;
}

interface ColorConfig {
  label: string;
  iconClass: string;
  /** Whether to use color identity icon styling (for multi) */
  isColorIdentity?: boolean;
}

const COLOR_CONFIG: Record<ColorFilterValue, ColorConfig> = {
  W: { label: 'White', iconClass: 'ms-w' },
  U: { label: 'Blue', iconClass: 'ms-u' },
  B: { label: 'Black', iconClass: 'ms-b' },
  R: { label: 'Red', iconClass: 'ms-r' },
  G: { label: 'Green', iconClass: 'ms-g' },
  multi: { label: 'Multicolor', iconClass: 'ms-ci ms-ci-5', isColorIdentity: true },
  colorless: { label: 'Colorless', iconClass: 'ms-c' },
};

export default function ColorFilterButton({ color, isActive, onClick }: ColorFilterButtonProps): ReactElement {
  const config = COLOR_CONFIG[color];

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'group flex h-8 w-8 items-center justify-center rounded-full transition-all',
        'focus-visible:ring-aether focus-visible:ring-offset-void focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        // Base styling
        isActive ? 'ring-aether ring-offset-void ring-2 ring-offset-2' : 'hover:bg-slate/50',
        // Background for inactive state
        !isActive && 'bg-storm/50',
      )}
      aria-label={`Filter by ${config.label}`}
      aria-pressed={isActive}
      title={config.label}
    >
      <i
        className={clsx(
          'ms text-xl',
          config.iconClass,
          // Use ms-cost for standard mana symbols (not for color identity icons like multi)
          !config.isColorIdentity && 'ms-cost',
          // White mana: add glow for visibility on dark background
          color === 'W' && 'drop-shadow-[0_0_4px_rgba(255,255,255,0.6)]',
          // Black mana: add subtle light glow for visibility
          color === 'B' && 'drop-shadow-[0_0_3px_rgba(180,180,180,0.4)]',
          // Colorless diamond: add subtle glow for visibility
          color === 'colorless' && 'drop-shadow-[0_0_3px_rgba(200,200,200,0.5)]',
          // Inactive state: slightly dimmed
          !isActive && 'opacity-70 group-hover:opacity-100',
        )}
        aria-hidden="true"
      />
    </button>
  );
}
